/**
 * The "Earn credits" rules, as pure data shaping — no imports, so a unit test can exercise it
 * without dragging the app's dependency graph in (see the Tests section of CLAUDE.md).
 *
 * Every amount the UI renders comes from the server (`GetRewardRules` via /api/reward-rules); the
 * client hard-codes none of them. That is the whole point of this module: a partial or failed read
 * resolves to `null` here, and the callers then omit the number rather than print a stale one. The
 * previous shape of this feature shipped a hard-coded `AGENTS_PER_REFERRAL = 10` and kept rendering
 * it long after a referral started paying 200 credits + 3 sub-agents + 3 briefs.
 */

/** The networks the program pays for, in the order the section lists them. */
export const SOCIAL_NETWORKS = ['INSTAGRAM', 'TIKTOK', 'FACEBOOK', 'LINKEDIN', 'X', 'OTHER'] as const;

export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

export interface RewardAmount {
  credits: number;
  subAgentRuns: number;
  agentMeetings: number;
}

export interface RewardRules {
  referrerReward: RewardAmount;
  refereeReward: RewardAmount;
  socialReward: RewardAmount;
  premiumConversionReward: RewardAmount;
}

export interface NetworkInfo {
  displayName: string;
  officialHandle: string;
  postRequirement?: string;
  requiresLink?: boolean;
}

export interface RewardRulesPayload {
  rules?: Partial<Record<keyof RewardRules, Partial<RewardAmount>>>;
  networks?: Partial<Record<string, NetworkInfo>>;
  claimed?: string[];
}

export interface NetworkRow {
  network: SocialNetwork;
  displayName: string;
  officialHandle: string;
  /** Already earned on this account — the row renders as done and is inert. */
  claimed: boolean;
  /**
   * Whether step 2 must ask for the invite link. Instagram and TikTok do not — a link in a caption
   * there is inert text, so we ask for the mention alone.
   *
   * Defaults to TRUE when the server does not say, which is the safe direction: an older server that
   * predates the field keeps the previous copy rather than silently telling every user to drop the
   * link. (The server's `postRequirement` says the same thing in prose, but it is a hardcoded English
   * string that feeds the vision prompt — rendering it would show English in 36 locales.)
   */
  requiresLink: boolean;
}

const isNetwork = (value: string): value is SocialNetwork =>
  (SOCIAL_NETWORKS as readonly string[]).includes(value);

/**
 * The rows to render: the served networks, in the program's own order, each carrying the handle to
 * follow and whether this account has already claimed it. A network the server did not describe is
 * dropped — the row's whole content is its display name and handle, and inventing either would put
 * a user in front of an account that may not be ours.
 */
export function networkRows(payload: RewardRulesPayload | undefined | null): NetworkRow[] {
  const networks = payload?.networks ?? {};
  const claimed = new Set((payload?.claimed ?? []).filter((value): value is string => typeof value === 'string'));

  return SOCIAL_NETWORKS.flatMap((network) => {
    const info = networks[network];
    if (!info || typeof info.displayName !== 'string' || info.displayName.trim() === '') return [];
    return [
      {
        network,
        displayName: info.displayName,
        officialHandle: typeof info.officialHandle === 'string' ? info.officialHandle : '',
        claimed: claimed.has(network),
        requiresLink: info.requiresLink !== false,
      },
    ];
  });
}

/** Networks the caller can still claim — used to decide whether the whole picker has anything to offer. */
export function unclaimedNetworks(rows: NetworkRow[]): NetworkRow[] {
  return rows.filter((row) => !row.claimed);
}

/**
 * One reward's amounts, or `null` when the read failed or the payload is incomplete.
 *
 * `null` means "omit the number", never "zero" and never a default: a wrong amount on a screen that
 * promises money is worse than a sentence with no amount in it.
 */
export function rewardAmount(
  rules: RewardRulesPayload['rules'] | undefined | null,
  key: keyof RewardRules,
): RewardAmount | null {
  const amount = rules?.[key];
  if (!amount) return null;
  const { credits, subAgentRuns, agentMeetings } = amount;
  if (![credits, subAgentRuns, agentMeetings].every((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0)) {
    return null;
  }
  return { credits: credits!, subAgentRuns: subAgentRuns!, agentMeetings: agentMeetings! };
}

/** The total earned from N referrals, or `null` while the per-referral amount is unknown. */
export function earnedCredits(referrerReward: RewardAmount | null, joined: number): number | null {
  if (!referrerReward) return null;
  return referrerReward.credits * Math.max(0, joined);
}

/** The generic failure line — a transport error, or a verdict we could not read. */
export const CLAIM_FAILURE_KEY = 'settings.earnCredits.reason.failed';

/**
 * The server's rejection reasons → the line the user reads. Every reason the claim route can return
 * is mapped; anything unrecognised (a newer server, a transport failure) falls back to the generic
 * line rather than rendering a raw enum.
 */
export const CLAIM_REASON_KEYS: Readonly<Record<string, string>> = {
  already_claimed: 'settings.earnCredits.reason.alreadyClaimed',
  not_a_post: 'settings.earnCredits.reason.notAPost',
  wrong_network: 'settings.earnCredits.reason.wrongNetwork',
  no_symphony_mention: 'settings.earnCredits.reason.noMention',
  unreadable: 'settings.earnCredits.reason.unreadable',
  not_wix_media: 'settings.earnCredits.reason.notWixMedia',
  verdict_unparseable: CLAIM_FAILURE_KEY,
};

export function claimReasonKey(reason: string | null | undefined): string {
  return (reason && CLAIM_REASON_KEYS[reason]) || CLAIM_FAILURE_KEY;
}
